import { normaliseLayoutName } from "@Helpers/constants";
import type { LayoutData } from "@ServerTypes/layoutView/layoutView.schema";
import { layoutKind } from "./layoutKind";

export const LAYOUT_NAME_ERROR = {
  EMPTY: "EMPTY",
  DUPLICATE: "DUPLICATE",
  RESERVED: "RESERVED",
} as const;

export type LayoutNameError = (typeof LAYOUT_NAME_ERROR)[keyof typeof LAYOUT_NAME_ERROR];

/** The message shown under the name field for each rejection reason. */
export const LAYOUT_NAME_ERROR_TEXT = {
  [LAYOUT_NAME_ERROR.EMPTY]: "Layout name is required",
  [LAYOUT_NAME_ERROR.DUPLICATE]: "A layout with this name already exists",
  [LAYOUT_NAME_ERROR.RESERVED]: "This name is reserved for a system layout",
} as const;

/**
 * Validates a layout name against its own namespace. One validator serves create, configure-save
 * and rename.
 *
 * The namespaces are separate, and that is the point:
 *  - PERSONAL names are unique PER USER, and the 4 system names are RESERVED here — a personal
 *    "All Modules" is an error.
 *  - SHARED names are unique across the portal, and system names are NOT reserved — a shared
 *    "All Modules" is allowed.
 *
 * So two users may each own a "Field Walk", and a personal and a shared layout may share a name.
 *
 * The check is sufficient in principle rather than merely a hint, because the list the flyout holds
 * is exactly (this user's personal ∪ every shared ∪ system) — both full namespaces. It is still racy
 * across tabs and users and can be stale, so the server validates too and its refusal is surfaced on
 * the same field.
 */
export function validateLayoutName({
  name,
  isShared,
  layouts,
  excludeId,
}: {
  name: string;
  /** The TARGET namespace, not the row's current one — a Move to Shared validates as shared. */
  isShared: boolean;
  /** The full layout list as loaded in the flyout. */
  layouts: LayoutData[];
  /** The row being renamed or reconfigured, so re-saving an unchanged name is not a self-collision. */
  excludeId?: string | undefined;
}): LayoutNameError | null {
  const candidate = normaliseLayoutName(name);
  if (!candidate) return LAYOUT_NAME_ERROR.EMPTY;

  // Every row already carrying this name. Reserved names are read off the loaded system rows rather
  // than a hardcoded list, so they cannot drift from what is stored and stay correct if a future
  // seed is localized; the server validates independently, so a failed fetch degrades to its refusal.
  const sameName = layouts.filter(
    (layout) => layout._id !== excludeId && normaliseLayoutName(layout.name ?? "") === candidate,
  );

  // The namespaces are checked separately — a name can legitimately exist in the other one, so this
  // must not collapse into "any row with this name".
  if (!isShared && sameName.some((layout) => layoutKind(layout) === "system")) {
    return LAYOUT_NAME_ERROR.RESERVED;
  }
  const clashKind = isShared ? "shared" : "personal";
  return sameName.some((layout) => layoutKind(layout) === clashKind) ? LAYOUT_NAME_ERROR.DUPLICATE : null;
}

/**
 * A name that is free in the target namespace, derived from `baseName` by appending " (2)", " (3)", …
 *
 * Duplicate actions copy the source name verbatim, so without this EVERY duplicate would collide the
 * moment name validation exists — and duplicating the shared "All Modules" into My Layouts would try
 * to mint a name reserved in the personal namespace.
 */
export function nextAvailableLayoutName({
  baseName,
  isShared,
  layouts,
}: {
  baseName: string;
  isShared: boolean;
  layouts: LayoutData[];
}): string {
  const trimmed = baseName.trim();
  let candidate = trimmed;
  // Terminates without needing a cap: each rejected suffix is blocked by a distinct existing layout,
  // and the list is finite.
  for (let suffix = 2; validateLayoutName({ name: candidate, isShared, layouts }) !== null; suffix += 1) {
    candidate = `${trimmed} (${suffix})`;
  }
  return candidate;
}
